import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import { IProduct, IProductDetailPriceBook, IProductReview } from '@/types/product';
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const PRODUCT_DETAIL_QUERY_KEY = 'product-detail' as const;
const PRODUCT_DETAIL_PRICEBOOK_QUERY_KEY = 'product-detail-pricebook' as const;
const PRODUCT_REVIEW_QUERY_KEY = 'product-review' as const;

export const productDetailQueryKeys = queryKeysFactory(PRODUCT_DETAIL_QUERY_KEY);
export const productDetailPriceBookQueryKeys = queryKeysFactory(PRODUCT_DETAIL_PRICEBOOK_QUERY_KEY);
export const productReviewQueryKeys = {
  ...queryKeysFactory(PRODUCT_REVIEW_QUERY_KEY),
  list: (productId: string, params?: HttpTypes.FindParams) =>
    [PRODUCT_REVIEW_QUERY_KEY, 'list', productId, params] as const,
};

export const useProductDetail = (
  slug?: string,
  token?: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<IProduct>,
      FetchError,
      HttpTypes.IResponse<IProduct>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productDetailQueryKeys.detail(slug as string),
    queryFn: async () => sdk.site.homepage.retrieveProductDetail(slug as string, token as string),
    enabled: !!slug && options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};

export const useGetProductDetail = (
  token?: string,
  options?: UseMutationOptions<HttpTypes.IResponse<IProduct>, FetchError, string>
) => {
  return useMutation({
    mutationFn: (payload) => {
      const locationId = Cookies.get('_location_id');
      return sdk.site.homepage.retrieveProductDetail(
        payload as string,
        token as string,
        locationId
      );
    },
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useProductDetailPriceBook = (
  slug?: string,
  token?: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<IProductDetailPriceBook[]>,
      FetchError,
      HttpTypes.IResponse<IProductDetailPriceBook[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: productDetailPriceBookQueryKeys.list(slug as string),
    queryFn: async () =>
      sdk.site.homepage.retrieveProductDetailPriceBook(slug as string, token as string),
    enabled: !!slug && options?.enabled,
    ...options,
  });

  return { ...data, ...rest };
};

export const useListProductReview = (
  productId: string,
  queryParams?: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.IResponse<IProductReview[]>,
      FetchError,
      HttpTypes.IResponse<IProductReview[]>,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  return useQuery({
    queryKey: productReviewQueryKeys.list(productId, queryParams),
    queryFn: async () => sdk.site.homepage.listProductReview(productId, queryParams),
    enabled: !!productId && options?.enabled,
    ...options,
  });
};
